import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import axiosInstance from "../../../axiosInstance";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { getJobPortalAuthConfig } from "../../../helpers/jobPortalAuth";

const DatabaseTransfer = () => {
  const [models, setModels] = useState([]);
  const [selectedModels, setSelectedModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResults, setImportResults] = useState([]);

  const allSelected = useMemo(
    () => models.length > 0 && selectedModels.length === models.length,
    [models.length, selectedModels.length]
  );

  const fetchModels = async () => {
    try {
      setLoading(true);
      const authConfig = await getJobPortalAuthConfig();
      const response = await axiosInstance.get("/admin/database/models", authConfig);
      const nextModels = response.data?.data || [];
      setModels(nextModels);
      setSelectedModels(nextModels.map((model) => model.key));
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load database models");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModels();
  }, []);

  const toggleModel = (modelKey) => {
    setSelectedModels((current) =>
      current.includes(modelKey)
        ? current.filter((key) => key !== modelKey)
        : [...current, modelKey]
    );
  };

  const toggleAllModels = () => {
    setSelectedModels(allSelected ? [] : models.map((model) => model.key));
  };

  const exportModels = async () => {
    if (!selectedModels.length) {
      toast.error("Select at least one model to export");
      return;
    }

    try {
      setExporting(true);
      const authConfig = await getJobPortalAuthConfig();
      const response = await axiosInstance.get("/admin/database/export", {
        ...authConfig,
        params: { models: allSelected ? "all" : selectedModels.join(",") },
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `database-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to export selected models");
    } finally {
      setExporting(false);
    }
  };

  const readImportFile = () =>
    new Promise((resolve, reject) => {
      if (!importFile) {
        reject(new Error("Please choose a JSON export file"));
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        try {
          resolve(JSON.parse(reader.result));
        } catch (error) {
          reject(new Error("Selected file is not valid JSON"));
        }
      };
      reader.onerror = () => reject(new Error("Unable to read selected file"));
      reader.readAsText(importFile);
    });

  const importModels = async () => {
    if (!selectedModels.length) {
      toast.error("Select at least one model to import");
      return;
    }

    try {
      setImporting(true);
      setImportResults([]);
      const payload = await readImportFile();
      const authConfig = await getJobPortalAuthConfig();
      const response = await axiosInstance.post(
        "/admin/database/import",
        {
          models: allSelected ? ["all"] : selectedModels,
          data: payload.data || payload,
        },
        authConfig
      );

      setImportResults(response.data?.data || []);
      toast.success(response.data?.message || "Import completed");
      fetchModels();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Unable to import data");
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />
        <div className="py-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
            <div>
              <h4 className="fw-bold mb-1">Database Import / Export</h4>
              <p className="text-muted small mb-0">
                Export or import selected database models from a JSON backup file.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={fetchModels}
              disabled={loading}
            >
              Refresh
            </button>
          </div>

          <div className="row g-3">
            <div className="col-xl-7">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">Models</h6>
                    <div className="form-check">
                      <input
                        id="select-all-models"
                        className="form-check-input"
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAllModels}
                      />
                      <label className="form-check-label" htmlFor="select-all-models">
                        Select all
                      </label>
                    </div>
                  </div>

                  {loading ? (
                    <p className="text-muted mb-0">Loading models...</p>
                  ) : (
                    <div className="row g-2">
                      {models.map((model) => (
                        <div className="col-md-6" key={model.key}>
                          <label className="border rounded p-3 w-100 d-flex align-items-start gap-2">
                            <input
                              className="form-check-input mt-1"
                              type="checkbox"
                              checked={selectedModels.includes(model.key)}
                              onChange={() => toggleModel(model.key)}
                            />
                            <span>
                              <span className="fw-semibold d-block">{model.label}</span>
                              <span className="text-muted small">
                                {model.count || 0} records
                              </span>
                            </span>
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-xl-5">
              <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                  <h6 className="mb-3">Export</h6>
                  <p className="text-muted small">
                    Downloads one JSON file containing every selected model.
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary w-100"
                    onClick={exportModels}
                    disabled={exporting || loading || !selectedModels.length}
                  >
                    {exporting ? "Exporting..." : `Export ${selectedModels.length} model(s)`}
                  </button>
                </div>
              </div>

              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <h6 className="mb-3">Import</h6>
                  <input
                    className="form-control mb-3"
                    type="file"
                    accept="application/json,.json"
                    onChange={(event) => setImportFile(event.target.files?.[0] || null)}
                  />
                  <button
                    type="button"
                    className="btn btn-warning w-100"
                    onClick={importModels}
                    disabled={importing || loading || !selectedModels.length || !importFile}
                  >
                    {importing ? "Importing..." : `Import ${selectedModels.length} model(s)`}
                  </button>
                  <p className="text-muted small mt-3 mb-0">
                    Existing records are updated by _id. New records are inserted.
                  </p>
                </div>
              </div>
            </div>

            {importResults.length > 0 && (
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <h6 className="mb-3">Import Results</h6>
                    <div className="table-responsive">
                      <table className="table align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Model</th>
                            <th>Total</th>
                            <th>Inserted</th>
                            <th>Updated</th>
                            <th>Failed</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importResults.map((result) => (
                            <tr key={result.key}>
                              <td>{result.label}</td>
                              <td>{result.total || 0}</td>
                              <td>{result.inserted || 0}</td>
                              <td>{result.updated || 0}</td>
                              <td>{result.failed || 0}</td>
                              <td>{result.skipped ? result.message : "Done"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default DatabaseTransfer;
